import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Tv } from "lucide-react";
import { useEffect, useState } from "react";

import { ownerApprovalsApi } from "../api/collab";
import { Badge, Banner, Button, Field, Modal, SkeletonRows, Textarea, formatRelative, useToast } from "../ds";

/** Owners never put a report on TV screens directly: they ask, an IT admin
 * approves (publishes the draft and adds the chosen screens) or rejects with a note. */
export function RequestTvPublishDialog({ open, onClose, reportId, reportName }: { open: boolean; onClose: () => void; reportId: string; reportName?: string }) {
  const qc = useQueryClient();
  const toast = useToast();
  const screens = useQuery({ queryKey: ["owner-screens"], queryFn: ownerApprovalsApi.screens, enabled: open, staleTime: 60_000 });
  const requests = useQuery({ queryKey: ["tv-requests", reportId], queryFn: () => ownerApprovalsApi.listTvRequests(reportId), enabled: open });
  const [groups, setGroups] = useState<string[]>([]);
  const [devices, setDevices] = useState<string[]>([]);
  const [note, setNote] = useState("");

  useEffect(() => {
    if (!open) {
      setGroups([]);
      setDevices([]);
      setNote("");
    }
  }, [open]);

  const send = useMutation({
    mutationFn: () => ownerApprovalsApi.requestTvPublish(reportId, { note, device_group_ids: groups, device_ids: devices }),
    onSuccess: () => {
      toast.success("Request sent", "IT will review the draft. Nothing changes on the screens until they approve.");
      qc.invalidateQueries({ queryKey: ["tv-requests", reportId] });
      setNote("");
    },
    onError: (e) => toast.error("Couldn't send the request", e instanceof Error ? e.message : undefined),
  });
  const cancel = useMutation({
    mutationFn: (id: string) => ownerApprovalsApi.cancelTvRequest(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["tv-requests", reportId] }),
    onError: (e) => toast.error("Couldn't cancel", e instanceof Error ? e.message : undefined),
  });
  const toggle = (list: string[], set: (v: string[]) => void, id: string) => set(list.includes(id) ? list.filter((x) => x !== id) : [...list, id]);
  const pending = (requests.data ?? []).find((r) => r.status === "pending");

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="lg"
      title={<span className="ds-row" style={{ gap: 8 }}><Tv size={18} /> Request TV publish{reportName ? ` — ${reportName}` : ""}</span>}
      description="IT reviews the current draft, publishes it and adds the screens you choose. You'll be notified of the decision."
      footer={
        <>
          <Button onClick={onClose}>Close</Button>
          <Button variant="primary" icon={Tv} loading={send.isPending} disabled={!!pending} onClick={() => send.mutate()}>Send request</Button>
        </>
      }
    >
      <div className="ds-stack">
        {pending ? (
          <Banner tone="info" title="Your request is waiting for IT" action={<Button size="sm" loading={cancel.isPending} onClick={() => cancel.mutate(pending.id)}>Cancel request</Button>}>
            Sent {formatRelative(pending.created_at)}{pending.stale ? " — you've changed the draft since, so IT will ask you to send it again." : "."}
          </Banner>
        ) : null}
        <div>
          <div className="ds-small ds-muted" style={{ marginBottom: 6 }}>Which screens? (optional — leave empty to just publish a new version of a report that is already on screens)</div>
          {screens.isLoading ? (
            <SkeletonRows rows={2} />
          ) : (
            <div className="ds-row" style={{ gap: 8, flexWrap: "wrap" }}>
              {(screens.data?.groups ?? []).map((g) => (
                <button key={g.id} type="button" className="ds-chip" aria-pressed={groups.includes(g.id)} onClick={() => toggle(groups, setGroups, g.id)}>{g.name}</button>
              ))}
              {(screens.data?.devices ?? []).filter((d) => !d.group_id).map((d) => (
                <button key={d.id} type="button" className="ds-chip" aria-pressed={devices.includes(d.id)} onClick={() => toggle(devices, setDevices, d.id)}>{d.name}</button>
              ))}
              {screens.data && screens.data.groups.length === 0 && screens.data.devices.length === 0 ? <span className="ds-small ds-muted">No screens are set up yet.</span> : null}
            </div>
          )}
        </div>
        <Field label="Note for IT" hint="What changed, and when it should go live.">
          <Textarea value={note} rows={3} maxLength={2000} onChange={(e) => setNote(e.target.value)} />
        </Field>
        {(requests.data ?? []).filter((r) => r.status !== "pending").length > 0 ? (
          <div>
            <div className="ds-small ds-muted" style={{ marginBottom: 6 }}>Earlier requests</div>
            {(requests.data ?? []).filter((r) => r.status !== "pending").slice(0, 5).map((r) => (
              <div key={r.id} className="ds-row" style={{ gap: 8, padding: "4px 0" }}>
                <Badge tone={r.status === "approved" ? "success" : "neutral"} dot>{r.status === "approved" ? "Approved" : r.status === "rejected" ? "Not approved" : "Cancelled"}</Badge>
                <span className="ds-small ds-muted">{formatRelative(r.decided_at ?? r.created_at)}</span>
                {r.decision_note ? <span className="ds-small">IT: {r.decision_note}</span> : null}
              </div>
            ))}
          </div>
        ) : null}
      </div>
    </Modal>
  );
}
